import IconButton from "components/atoms/button/IconButton";
import Headline from "components/atoms/headline/Headline";
import Icon from "components/atoms/icon/Icon";
import TabsSwitch from "components/molecules/tabs/TabsSwitch";
import DashboardBoxLayout from "components/templates/dashboard-box-layout/DashboardBoxLayout";
import { IconType } from "lib/helpers/constants/iconTypes";
import { ReactInstance, RefObject, useEffect, useMemo, useRef, useState } from "react";
import EmployeeProfileList from "./employee-profile-list/EmployeeProfileList";
import { Column } from "react-table";
import EmployeeJobIcon from "components/atoms/icon/icon-type/EmployeeJobIcon";
import EmployeeEmailIcon from "components/atoms/icon/icon-type/EmployeeEmailIcon";
import Input from "components/atoms/input/Input";
import { useEmployeSelfProfile } from "lib/hooks/queries/Employee/useEmployeSelfProfile";
import { useNavigate, useParams } from "react-router-dom";
import LoadingScreen from "components/atoms/loading-screen/LoadingScreen";
import { useThemeContext } from "lib/context/ThemeContext/ThemeContext";
import { useModalContext } from "lib/context/ModalContext/ModalContext";
import { useReactToPrint } from "react-to-print";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import ErrorState from "components/atoms/error-state/ErrorState";
import useMediaQuery from "lib/hooks/shared/useMediaQuery";
import { useTranslation } from "react-i18next";
import Salary from "./salary-tabs/Salary";
import EndContract from "./end-contract/EndContract";
import ConditionalRender from "components/atoms/conditional-render/ConditionalRender";
import { usePermissions } from "lib/hooks/shared/usePermissions";
import TargetAndBonusTab from "./target-bonus-tabs/TargetAndBonusTab";
import { languageData } from "lib/helpers/constants/languageData";
import UpdateEmployeeProfile from "./update-employee-profile/UpdateEmployeeProfile";
import AddNewSalaryModule from "../finance-salaries/add-new-salary-module/AddNewSalaryModule";
import ToggleSwitch from "components/molecules/toggle-switch/ToggleSwitch";
import { useToggleUserStatus } from "lib/hooks/mutations/Employee/useToggleUserStatus";
import Requests from "./requests/Requests";
import Button from "components/atoms/button/Button";
import { ShowPasswordIcon } from "components/atoms/icon/export";
import { useSalaryPaperHistory } from "lib/hooks/queries/Employee/useSalaryPaperHistory";
import NoDataBoundary from "components/atoms/no-data-boundary/NoDataBoundary";
import EmptyEntity from "components/atoms/empty-entity/EmptyEntity";

const EmployeeProfileView = () => {

  const [selectedTabSwitch, setSelectedTabSwitch] = useState<number>(0);
  const { id } = useParams();

  const { data, isLoading, isError } = useEmployeSelfProfile(id);

  const { data:paperData } = useSalaryPaperHistory(id);

  const [activeTab, setActiveTab] = useState<number>(0);
  const navigate = useNavigate();
  const { themeColors } = useThemeContext();
  const { confirm } = useModalContext();
  const componentRef = useRef<ReactInstance | null>(null);
  const token: any = localStorage.getItem("token");
  const { t } = useTranslation();

  const hasPermissionToviewAllEmployee = usePermissions(['employee_management_view']);
  const hasPermissionToEditEmployee = usePermissions(['employee_management_edit']);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  if (isError) {
    navigate("/hr");
  }

  const dummyData = useMemo(
    () => [
      {
        date: "Date",
        checkin: "09:28 AM",
        checkout: "07:00 PM",
        break: "00:30 Min",
        workinghours: "09:02 Hrs",
        joiningdate: "16/08/2013",
        status: "Active",
      },
      {
        date: "Date",
        checkin: "09:28 AM",
        checkout: "07:00 PM",
        break: "00:30 Min",
        workinghours: "09:02 Hrs",
        joiningdate: "16/08/2013",
        status: "Active",
      },
    ],
    []
  );

  const columns = useMemo<Column<any>[]>(
    () => [
      {
        Header: `${t("Date")}`,
        accessor: "date",
      },
      {
        Header: `${t("Check In")}`,
        accessor: "checkin",
      },
      {
        Header: `${t("Check out")}`,
        accessor: "checkout",
      },
      {
        Header: `${t("Break")}`,
        accessor: "break",
      },
      {
        Header: `${t("Working Hours")}`,
        accessor: "workinghours",
      },
      {
        Header: `${t("Status")}`,
        accessor: "status",
        Cell: ({ row: { original } }) => (
          <div className="bg-[#2CC95B] bg-opacity-[10%] text-[#2CC95B] max-w-[66px] flex justify-center items-center p-1 rounded-[4px]">
            {original.status}
          </div>
        ),
      },
    ],
    [t]
  ) as Column<any>[];

  const showProfessionalInfo = useMediaQuery(
    "(min-width: 1024px) and (max-width: 1450px)"
  );

  const gridResponse = useMediaQuery(
    "(min-width: 1024px) and (max-width: 1450px)"
  );

  const renderTabs = () => {
    switch (activeTab) {
      case 0:
        return (

          <>
            <TabsSwitch
              ActiveTab={selectedTabSwitch}
              setSelectedTab={(id) => setSelectedTabSwitch(id)}
              classes={{
                iconAndTextWrapperClass:
                  "text-[13px] lg:text-[14px] xl:text-[16px] flex items-center",
                text: "!pl-1 !pb-[8px]",
                unActiveTabClass: "!pl-1 !pb-[8px] w-full",
                tabsClass:
                  "flex flex-col  justify-between w-full lg:flex-row  lg:whitespace-nowrap",
                subContainerClass:
                  "flex flex-col lg:flex-row justify-between lg:items-center",
              }}
              tabs={[
                {
                  id: 0,
                  name: showProfessionalInfo
                    ? "Personal Info"
                    : "Personal Information",
                  Icon: (
                    <Icon
                      iconType={IconType.PROFILE_ICON}
                      className="w-[18px] xl:w-[25px]"
                      color={
                        selectedTabSwitch === 0 ? themeColors.primary : ""
                      }
                    />
                  ),
                },
                {
                  id: 1,
                  name: showProfessionalInfo
                    ? "Professional Info"
                    : "Professional Information",
                  Icon: (
                    <Icon
                      className="w-[18px] xl:w-[25px]"
                      iconType={IconType.PROFESIONAL_INFORMATION_ICON}
                      color={
                        selectedTabSwitch === 1 ? themeColors.primary : ""
                      }
                    />
                  ),
                },
                {
                  id: 2,
                  name: "Documents",
                  Icon: (
                    <Icon
                      className="w-[18px] xl:w-[25px]"
                      iconType={IconType.DOCUMENT_ICON}
                      color={
                        selectedTabSwitch === 2 ? themeColors.primary : ""
                      }
                    />
                  ),
                },
                // {
                //   id: 3,
                //   name: showProfessionalInfo
                //     ? "Access"
                //     : "Account Accesssssssssssss",
                //   Icon: (
                //     <Icon
                //       className="w-[18px] xl:w-[25px]"
                //       iconType={IconType.ACCOUNT_ACCESS_ICON}
                //       color={
                //         selectedTabSwitch === 3 ? themeColors.primary : ""
                //       }
                //     />
                //   ),
                // },
              ]}
            />
            {selectedTabSwitch === 0 && (
              <div className="grid grid-cols-1  lg:grid-cols-2 gap-[25px]">
                <Input
                  label={t("First Name")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={data?.data?.name ?? `${t("Not Available")}`}
                  disabled={true}
                />
                <Input
                  label={t("Last Name")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={data?.data?.last_name ?? `${t("Not Available")}`}
                  disabled={true}
                />
                <Input
                  label={t("Mobile Number")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  disabled={true}
                  value={
                    data?.data?.user_information?.phone ?? `${t("Not Available")}`
                  }
                />
                <Input
                  label={t("Email Address")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={data?.data?.email ?? `${t("Not Available")}`}
                  disabled={true}
                />
                <Input
                  label={t("Date of Birth")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.birthdate ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Gender")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.gender ?? `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Language")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    languageData.find((item: any) => item.value === data?.data?.user_information.language)?.label ?? `${t("Not Available")}`
                  }
                  disabled={true}
                />


                <Input
                  label={t("Nationality")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.nationality ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Address")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.address ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                {/* <Input
                  label={t("City")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.city ?? `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("State")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.state ?? `${t("Not Available")}`
                  }
                  disabled={true}
                /> */}
                <Input
                  label={t("Zip Code")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.zip_code ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Marital Status")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.marital_status ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Children")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information.children ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
              </div>
            )}
            {selectedTabSwitch === 1 && (
              <div className="grid grid-cols-2 gap-5">
                <Input
                  label={t("Employee Number")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={data?.data?.employe_number ?? `${t("Not Available")}`}
                  disabled={true}
                />

                <Input
                  label={t("Employee Type")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.employee_type ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />

                <Input
                  label={t("Department")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.department ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Position")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.position ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Working Days")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.working_days ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Joining Date")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.working_days ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Office Location")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.office_location ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Betreibung")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.betreibung ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("AHV Number")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.ahv_number ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />

                <Input
                  label={t("Finma Number")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.finma_number ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Bank Name")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.bank_name ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
                <Input
                  label={t("Bank Number")}
                  classes={{
                    label:
                      "text-[#A2A1A8] font-inet-regular text-[14px] leading-[22px] !mb-0",
                  }}
                  className="!border-0 !border-b border-b-[#A2A1A81A] rounded-none !indent-0 !pl-0 disabled:bg-transparent"
                  value={
                    data?.data?.user_information?.bank_number ??
                    `${t("Not Available")}`
                  }
                  disabled={true}
                />
              </div>
            )}
            {selectedTabSwitch === 2 && (
              <NoDataBoundary
                  condition={paperData && paperData?.length > 0}
                 fallback={<EmptyEntity name={t("Documents")} />}
              >
              <div className="grid grid-cols-2 gap-[20px]">
                {paperData?.map((item: any, index: any) => (
                  <div
                    key={index}
                    className="flex flex-row w-full items-center border-[1px] rounded-[15px] justify-between p-4"
                  >
                    <div className="text-[#282D46] text-[16px] font-inter-regular leading-[24px]">
                      {item?.name}
                    </div>
                    <div className="flex flex-row items-center gap-3">
                      <Button
                        disabled={!item?.pdf}
                        onClick={() => {
                          const url = `${process.env.REACT_APP_API_URL}hr/salary/show-salary-paper/${item?.pdf}`;
                          window.open(url, '_blank');
                        }}
                      >
                        <ShowPasswordIcon color="#282D46" />
                      </Button>
                      {/* <Button
                        disabled={
                          !data?.data?.user_information?.salary_slips_file
                        }
                        onClick={() => {
                          fetch(
                            `http://127.0.0.1:8000/api/hr/salary/show-salary-paper/${item?.pdf}`
                          )
                            .then((response) => response.blob())
                            .then((blob) => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.style.display = "none";
                              a.href = url;
                              a.download = "";
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            })
                            .catch((error) => console.error(error));
                        }}
                      >
                        <Icon iconType={IconType.PROFILE_DOWNLOAD_ICON} />
                      </Button> */}
                    </div>
                  </div>
                ))}
              </div>
              </NoDataBoundary>
            )}
            {selectedTabSwitch === 3 && (
              <div className="grid grid-cols-2 gap-5">
              </div>
            )}

          </>
        );
      case 1:
        return (
          <EmployeeProfileList columns={columns} data={dummyData} />
        );
      case 2:
        return (
          <EmployeeProfileList columns={columns} data={dummyData} />
        );
      case 3:
        return (
          <EmployeeProfileList columns={columns} data={dummyData} />
        );
      case 4:
        return (
          <EmployeeProfileList columns={columns} data={dummyData} />
        );
      case 5:
        return (
          <Salary userData={data?.data} />
        );
      case 6:
        return (
          <TargetAndBonusTab />
        );
      case 7:
        return (
          <Requests />
        );
      default:
        return null;
    }
  };

  const hasPermissionToCreateRole = usePermissions(['roles_management_create']);
  const hasPermissionToCreateSalary = usePermissions(['salaries_create']);

  const [IsEndContractModalOpen, setIsEndContractModalOpen] = useState<boolean>(false)

  const [UpdateEmployeeModalData, setUpdateEmployeeModalData] = useState<any>(null)
  const [UpdateEmployeeModalStep, setUpdateEmployeeModalStep] = useState(1)

  const [IsSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
  const [SalaryModalStep, setSalaryModalStep] = useState(1)

  //eslint-disable-next-line
  const showDocument = async (file: string) => {
    const isImage = (file: string) => {
      const ext = file?.split(".")?.pop()?.toLowerCase();
      return ext === "jpg" || ext === "jpeg";
    };
    try {
      await confirm({
        title: `${t("Application")}`,
        classes: {
          modal: "md:min-w-[750px] lg:w-[973px]",
        },
        showCloseButton: true,
        showBackButton: true,
        customComponent: () => {
          return (
            <div>
              <div
                ref={componentRef as RefObject<HTMLDivElement>}
                style={{ height: "100%" }}
              >
                {isImage(file) ? (
                  <img
                    src={`${process.env.REACT_APP_API_URL}hr/show-user-document/${file}`}
                    alt="img"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Worker
                    workerUrl={`https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`}
                  >
                    <Viewer
                      renderError={(error) => (
                        <>
                          <ErrorState error={error}>
                            {t("Something went wrong. Please try again later.")}
                          </ErrorState>
                        </>
                      )}
                      httpHeaders={{
                        Authorization: `Bearer ${token}`,
                      }}
                      renderLoader={() => (
                        <div className="min-h-[500px]">
                          <LoadingScreen />
                        </div>
                      )}
                      fileUrl={`${process.env.REACT_APP_API_URL}hr/show-user-document/${file}`}
                    />
                  </Worker>
                )}
              </div>
              <div className="flex justify-end gap-[26px] mt-5">
                {isImage(file) ? (
                  ""
                ) : (
                  <IconButton
                    onClick={() => handlePrint()}
                    type="button"
                    secondary={true}
                    icon={
                      <Icon
                        iconType={IconType.PRINT_ICON}
                        color={themeColors.primary}
                      />
                    }
                  >
                    {t("Print")}
                  </IconButton>
                )}
                <IconButton
                  onClick={() => {
                    fetch(
                      `${process.env.REACT_APP_API_URL}hr/show-user-document/${file}`
                    )
                      .then((response) => response.blob())
                      .then((blob) => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.style.display = "none";
                        a.href = url;
                        a.download = "";
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      })
                      .catch((error) => console.error(error));
                  }}
                  type="button"
                  icon={<Icon iconType={IconType.DOWNLOAD_ICON} />}
                >
                  {t("Download")}
                </IconButton>
              </div>
            </div>
          );
        },
      });
    } catch (err) {
      return null;
    }
  };

  const [IsChecked, setIsChecked] = useState(false)

  useEffect(() => {
    if (!data) {
      return
    }
    setIsChecked(data?.data?.is_deactive === 0 ? true : false)


  }, [data])

  const hasPermissionToViewAbsence = usePermissions(['absence_requests_view'])
  const hasPermissionToViewOwnAbsence = usePermissions(['absence_requests_view_view_own'])

  const hasPermissionToViewOrders = usePermissions(['orders_request_view'])
  const hasPermissionToViewOwnOrders = usePermissions(['orders_request_view_view_own'])
  const { mutateAsync: toggleUserStatus, isLoading: toggleUserStatusLoading } = useToggleUserStatus()

  const toggleStatus = async (e: any) => {


    toggleUserStatus({
      id: id,
    })
      .then(() => {

        setIsChecked(!e.target.checked);

      })
      .catch(() => { });


  };

  if (isLoading) {
    return <LoadingScreen />;
  }
  return (
    <div>
      <Headline title={t("Employees Management")} />
      <div className={`mb-5 mt-5`}>
        <div>
          <div className="bg-white px-[20px] py-5 min-h-[150px] flex-col sm:flex-row border-[1px] border-[#F2F4F7] gap-4 md:gap-[5px] 2xl:gap-[30px] rounded-[20px] flex items-start sm:items-center">
            <div className="flex-none border-[2px] border-[--theme] rounded-full border-r-[#667085] border-t-[#667085]">
              <img
                src={
                  data?.data?.user_information?.profil_picture
                    ? `${process.env.REACT_APP_API_URL}profile-picture/${data.data.user_information.profil_picture}`
                    : "https://st3.depositphotos.com/9998432/13335/v/450/depositphotos_133351928-stock-illustration-default-placeholder-man-and-woman.jpg"
                }
                className="object-cover rounded-full w-[70px] h-[70px] 2xl:w-[80px] 2xl:h-[80px] p-[6px]"
                alt="Profile"
                loading="lazy"
              />
            </div>
            <div className=" flex flex-col gap-2">
              <div>
                <Headline className="!mb-0 !mt-0" title={data?.data?.name + ' ' + data?.data?.last_name} />
              </div>
              <div className="flex flex-row gap-2 items-center">
                <EmployeeJobIcon />
                <p className="text-[#7D8592] font-inter-regular">
                  {data?.data?.email ?? `${t("Not Available")}`}
                </p>
              </div>
              <div className="flex flex-row gap-2">
                <EmployeeEmailIcon />
                <p className="text-[#7D8592] font-inter-regular">
                  {data?.data?.user_information?.phone ?? `${t("Not Available")}`}
                </p>
              </div>
            </div>
            <div className="sm:ml-auto self-start">
              <ConditionalRender condition={hasPermissionToEditEmployee}>
                <IconButton
                  icon={<Icon iconType={IconType.EDIT_ICON} color="white" />}
                  onClick={() => { setUpdateEmployeeModalData({ ...data.data, isOpen: true, isUpdating: true, user_id: id }); }}
                >
                  {t("Update Profile")}
                </IconButton>
              </ConditionalRender>
            </div>
          </div>

        </div>

      </div>
      <DashboardBoxLayout className="p-[20px] xl:p-[31px]">
        <div
          className={`grid grid-cols-1  lg:grid-cols-[200px_1fr] lg:gap-[10px]  xl:grid-cols-[242px_1fr] gap-4  xl:gap-[35px] items-start ${gridResponse ? "grid-cols-1" : ""
            }`}
        >
          <div className="row-span-2">
            <div className="w-full xl:w-[242px] rounded-[10px] border border-[#A2A1A833] white-space-nowrap ">
              <div
                onClick={() => {
                  setActiveTab(0);
                }}
                className={`px-6 py-4   rounded-t-[10px] flex gap-3 items-center ${activeTab === 0
                  ? `text-[#fff] bg-[--theme] font-inter-semibold`
                  : "text-primaryTextColor font-inter-light bg-white cursor-pointer"
                  }`}
              >
                <Icon
                  iconType={IconType.PROFILE_ICON}
                  color={activeTab === 0 ? "#fff" : "#282D46"}
                />
                <span className="text-[14px] xl:text-[16px]">{t("Profile")}</span>
              </div>
              <div
                onClick={() => {
                  setActiveTab(5);
                }}
                className={`px-6 py-4 flex gap-3 items-center ${activeTab === 5
                  ? `text-[#fff] bg-[--theme] font-inter-semibold`
                  : "text-primaryTextColor font-inter-light bg-white cursor-pointer"
                  }`}
              >
                <Icon
                  iconType={IconType.SALARY_ICON}
                  color={activeTab === 5 ? "#fff" : "#282D46"}
                />
                <span className="text-[14px] xl:text-[16px]">{t("Salary")}</span>
              </div>
              <div
                onClick={() => {
                  setActiveTab(6);
                }}
                className={`px-6 py-4 flex gap-3 rounded-b-[10px] items-center ${activeTab === 6
                  ? `text-[#fff] bg-[--theme] font-inter-semibold`
                  : "text-primaryTextColor font-inter-light bg-white cursor-pointer"
                  }`}
              >
                <Icon
                  iconType={IconType.TARGET_ICON}
                  color={activeTab === 6 ? "#fff" : "#282D46"}
                />
                <span className="text-[14px] xl:text-[16px]">{t("Targets & Bonus")}</span>
              </div>
              <ConditionalRender condition={hasPermissionToViewAbsence || hasPermissionToViewOwnAbsence || hasPermissionToViewOrders || hasPermissionToViewOwnOrders}>
              <div
                onClick={() => {
                  setActiveTab(7);
                }}
                className={`px-6 py-4 flex gap-3 rounded-b-[10px] items-center ${activeTab === 7
                  ? `text-[#fff] bg-[--theme] font-inter-semibold`
                  : "text-primaryTextColor font-inter-light bg-white cursor-pointer"
                  }`}
              >
                <Icon
                  iconType={IconType.REQUESTS_ICON}
                  color={activeTab === 7 ? "#fff" : "#282D46"}
                />
                <span className="text-[14px] xl:text-[16px]">{t("Requests")}</span>
              </div>
              </ConditionalRender>
              {/* <div
                onClick={() => {
                  setActiveTab(2);
                }}
                className={`px-6 py-4 flex gap-3 items-center ${activeTab === 2
                  ? `text-[#fff] bg-[--theme] font-inter-semibold`
                  : "text-primaryTextColor font-inter-light bg-white cursor-pointer"
                  }`}
              >
                <Icon
                  iconType={IconType.ATTENDANCE_ICON}
                  color={activeTab === 2 ? "#fff" : undefined}
                />

                <span className="text-[14px] xl:text-[16px]">{t("Attendance")}</span>
              </div>
              <div
                onClick={() => {
                  setActiveTab(3);
                }}
                className={`px-6 py-4 flex gap-3 items-center ${activeTab === 3
                  ? `text-[#fff] bg-[--theme] font-inter-semibold`
                  : "text-primaryTextColor font-inter-light bg-white cursor-pointer"
                  }`}
              >
                <Icon
                  iconType={IconType.ACTIVITY_LOG_ICON}
                  color={activeTab === 3 ? "#fff" : undefined}
                />

                <span className="text-[14px] xl:text-[16px]">{t("Projects")}</span>
              </div>
              <div
                onClick={() => {
                  setActiveTab(4);
                }}
                className={`px-6 py-4 rounded-b-[10px] flex gap-3 items-center ${activeTab === 4
                  ? `text-[#fff] bg-[--theme] font-inter-semibold`
                  : "text-primaryTextColor font-inter-light bg-white cursor-pointer"
                  }`}
              >
                <Icon
                  iconType={IconType.LEAVE_ICON}
                  color={activeTab === 4 ? "#fff" : undefined}
                />

                <span className="text-[14px] xl:text-[16px]">{t("Leave")}</span>
              </div> */}
            </div>
          </div>
          <div>
            {/* <ConditionalRender condition={hasPermissionToviewAllEmployee && hasPermissionToEditEmployee}>
              <div className="mb-5 text-end">
                <span onClick={() => { setIsEndContractModalOpen(true) }} className="text-[#D61616] font-inter-medium underline cursor-pointer">{t('End Contract')}</span>
              </div>
            </ConditionalRender> */}
            {renderTabs()}
          </div>
        </div>
      </DashboardBoxLayout>

      <UpdateEmployeeProfile
        headerComponent={
          {
            1: <ConditionalRender condition={hasPermissionToviewAllEmployee && hasPermissionToEditEmployee}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <ToggleSwitch
                    checked={IsChecked}
                    onChange={toggleStatus}
                    disabled={toggleUserStatusLoading}
                    classes={{ label: "!mb-0" }}
                  />
                  <div>
                    {IsChecked ? t("Active") : t("Inactive")}
                  </div>
                </div>
                <div className="text-end">
                  <span onClick={() => {
                    setIsEndContractModalOpen(true);
                    setUpdateEmployeeModalData(null)
                  }}
                    className="text-[#D61616] font-inter-medium underline cursor-pointer">
                    {t('End Contract')}
                  </span>
                </div>
              </div>
            </ConditionalRender>,
            2:
              <ConditionalRender condition={hasPermissionToCreateRole}>
                <div className="gap-3 flex items-center cursor-pointer" onClick={() => { setUpdateEmployeeModalStep(8) }}>
                  <Icon iconType={IconType.PLUS_ICON} color="var(--theme)" />
                  <span className="font-inter-semibold text-[--theme]">{t('New Role Model')}</span>
                </div>
              </ConditionalRender>,
            3:
              <ConditionalRender condition={hasPermissionToCreateSalary}>
                <div className="gap-3 flex items-center cursor-pointer" onClick={() => { setIsSalaryModalOpen(true) }}>
                  <Icon iconType={IconType.PLUS_ICON} color="var(--theme)" />
                  <span className="font-inter-semibold text-[--theme]">{t('New Salary Model')}</span>
                </div>
              </ConditionalRender>
            ,
          }[UpdateEmployeeModalStep]
        }
        clearData={true}
        hocCurrentStep={1}
        currentStep={UpdateEmployeeModalStep}
        setCurrentStep={setUpdateEmployeeModalStep}
        data={UpdateEmployeeModalData}
        onConfirm={() => setUpdateEmployeeModalData(null)}
        hocisOpen={UpdateEmployeeModalData?.isOpen}
        hocToggleModal={() => setUpdateEmployeeModalData(null)}
        hocTitle={data?.data?.name + ' ' + data?.data?.last_name}
        setStepOnClose={1}
        setHocCurrentStep={setUpdateEmployeeModalStep}
        hocClasses={{
          modalClassName: " w-full lg:max-w-[950px]",
          titleClassName: UpdateEmployeeModalStep === 1 ? "" : "ml-[35px]",
          headerComponentClassName: UpdateEmployeeModalStep === 1 ? '!ml-0 flex-1' : ''
        }}
      />
      <EndContract
        clearData={true}
        hocCurrentStep={1}
        user_id={data?.data?.id}
        onConfirm={() => setIsEndContractModalOpen(false)}
        hocisOpen={IsEndContractModalOpen}
        hocToggleModal={() => setIsEndContractModalOpen(false)}
        hocTitle={t('End Contract') + " : " + data?.data?.name + ' ' + data?.data?.last_name}
        hocClasses={{
          modalClassName: "w-full md:max-w-[500px]",
        }}
      />
      <AddNewSalaryModule
        canGoBackHr={true}
        clearData={true}
        setStepOnClose={1}
        setHocCurrentStep={setSalaryModalStep}
        hocCurrentStep={SalaryModalStep}
        onConfirm={() => { setIsSalaryModalOpen(false); setSalaryModalStep(1) }}
        setFormCurrentStep={setSalaryModalStep}
        currentStep={SalaryModalStep}
        hocisOpen={IsSalaryModalOpen}
        hocToggleModal={() => { setIsSalaryModalOpen(false); setSalaryModalStep(1) }}
        hocTitle={
          SalaryModalStep === 4
            ? t("Are you sure?")
            : t("Add New Salary Module")
        }
        hocClasses={{
          modalClassName: `w-full ${SalaryModalStep === 4 ? "md:max-w-[560px]" : "md:max-w-[1000px]"
            } `,
          titleClassName: SalaryModalStep === 4 ? "" : "ml-[35px]",
        }}
      />

    </div>
  );
};

export default EmployeeProfileView;
